<template>
    <md-content class="md-scrollbar">
        <div class="dashboard md-layout md-gutter md-alignment-horizontal" ref="DashboardContainer">
            <div
                v-for="cell in cells"
                :key="cell.id"
                class="md-layout-item"
                v-bind:class=cell.cellSizeString>
                <template v-if="cell.type === 'table'">
                    <DashboardTable :cell="cell" @save-csv="saveCsv(cell.data, cell.options)" />
                </template>

                <template v-else-if="cell.type === 'text'">
                    <md-card md-with-hover>
                        <md-card-header v-if="cell.options.title.display === true">
                            <h1 class="md-title">{{ cell.options.title.text }}</h1>
                        </md-card-header>
                        <md-card-content>
                            {{ cell.data }}
                        </md-card-content>
                        <md-card-actions>
                            <md-menu md-size="medium" md-direction="bottom-end">
                                <md-button class="md-icon-button" md-menu-trigger>
                                    <md-icon>file_download</md-icon>
                                </md-button>
                            </md-menu>
                            <md-button>
                                actions
                            </md-button>
                        </md-card-actions>
                    </md-card>
                </template>

                <template v-else-if="cell.type == 'choropleth'">
                    <md-card md-with-hover>
                        <md-card-header v-if="cell.options.title.display === true">
                            <h1 class="md-title">{{ cell.options.title.text }}</h1>
                            <DashboardIndicatorSelect
                                :indicators="cell.indicatorKeysAndNames.names"
                                :selectedIndicatorName="cell.selectedIndicatorName"
                                @user-changed-indicator="(newIndicator) => updateSelectedIndicatorForCell(newIndicator, cell.id)"
                                />
                        </md-card-header>
                        <md-card-content>
                            <ChoroplethGeoChart
                                :data="cell.data"
                                :bbox="cell.bbox"
                                :selectedIndicatorKey="cell.selectedIndicatorKey"
                                :selectedIndicatorName="cell.selectedIndicatorName"
                                />
                        </md-card-content>
                        <md-card-actions>
                            <md-menu md-size="medium" md-direction="bottom-end">
                                <md-button class="md-icon-button" md-menu-trigger>
                                    <md-icon>file_download</md-icon>
                                </md-button>
                                <md-menu-content>
                                    <md-menu-item v-on:click="saveFeatureCollectionAsCsv(cell.data.datasets[0].data, cell.options)">Download as CSV</md-menu-item>
                                    <md-menu-item v-on:click="saveFeatureCollectionAsGeoJson(cell.data.datasets[0].data, cell.options)">Download as GeoJSON</md-menu-item>
                                </md-menu-content>
                            </md-menu>
                        </md-card-actions>
                    </md-card>
                </template>

                <template v-else-if="cell.type == 'choropleth table'">
                    <md-card md-with-hover>
                        <md-card-header v-if="cell.options.title.display === true">
                            <h1 class="md-title">{{ cell.options.title.text }}</h1>
                        </md-card-header>
                        <md-card-content>
                            <ChoroplethGeoChart
                                :data="cell.data"
                                :bbox="cell.bbox"
                                :isTable="true"
                                />
                        </md-card-content>
                        <md-card-actions>
                            <md-menu md-size="medium" md-direction="bottom-end">
                                <md-button class="md-icon-button" md-menu-trigger>
                                    <md-icon>file_download</md-icon>
                                </md-button>
                                <md-menu-content>
                                    <md-menu-item v-on:click="saveFeatureCollectionAsCsv(cell.data.datasets[0].data, cell.options)">Download as CSV</md-menu-item>
                                    <md-menu-item v-on:click="saveFeatureCollectionAsGeoJson(cell.data.datasets[0].data, cell.options)">Download as GeoJSON</md-menu-item>
                                </md-menu-content>
                            </md-menu>
                        </md-card-actions>
                    </md-card>
                </template>

                <template v-else-if="cell.type == 'stacked bar'">
                    <md-card md-with-hover>
                        <md-card-header v-if="cell.options.title.display === true">
                            <h1 class="md-title">{{ cell.options.title.text }}</h1>
                            <DashboardIndicatorSelect
                                :indicators="cell.indicatorKeysAndNames.names"
                                :selectedIndicatorName="cell.selectedIndicatorName"
                                @user-changed-indicator="(newIndicator) => updateSelectedIndicatorForCell(newIndicator, cell.id)"
                                />
                        </md-card-header>
                        <md-card-content>
                            <DashboardStackedBarChart @stacked-bar-chart="(chart) => cell.chart = chart"
                                :data="cell.data"
                                :selectedIndicatorName="cell.selectedIndicatorName"
                                />
                        </md-card-content>
                        <md-card-actions>
                            <md-menu md-size="medium" md-direction="bottom-end">
                                <md-button class="md-icon-button" md-menu-trigger>
                                    <md-icon>file_download</md-icon>
                                </md-button>
                                <md-menu-content>
                                    <md-menu-item v-on:click="saveFeatureCollectionAsCsv(cell.data.datasets[0].data, cell.options)">Download as CSV</md-menu-item>
                                    <md-menu-item v-on:click="saveFeatureCollectionAsGeoJson(cell.data.datasets[0].data, cell.options)">Download as GeoJSON</md-menu-item>
                                    <md-menu-item v-on:click="saveAsPng(cell.options, cell.chart, `stacked-bar`)">Download as PNG</md-menu-item>
                                </md-menu-content>
                            </md-menu>
                        </md-card-actions>
                    </md-card>
                </template>

                <template v-else-if="cell.type == 'strata table'">
                    <md-card md-with-hover>
                        <md-card-header v-if="cell.options.title.display === true">
                            <h1 class="md-title">{{ cell.options.title.text }}</h1>
                        </md-card-header>
                        <md-card-content>
                            <TablesChart
                                :data="cell.data"
                                />
                        </md-card-content>
                        <md-card-actions>
                            <md-menu md-size="medium" md-direction="bottom-end">
                                <md-button class="md-icon-button" md-menu-trigger>
                                    <md-icon>file_download</md-icon>
                                </md-button>
                                <md-menu-content>
                                    <md-menu-item v-on:click="saveFeatureCollectionAsCsv(cell.data.datasets[0].data, cell.options)">Download as CSV</md-menu-item>
                                    <md-menu-item v-on:click="saveFeatureCollectionAsGeoJson(cell.data.datasets[0].data, cell.options)">Download as GeoJSON</md-menu-item>
                                </md-menu-content>
                            </md-menu>
                        </md-card-actions>
                    </md-card>

                </template>

                <template v-else-if="cell.type == 'tree chart'">
                    <md-card md-with-hover>
                        <md-card-header v-if="cell.options.title.display === true">
                            <h1 class="md-title">{{ cell.options.title.text }}</h1>
                        </md-card-header>
                        <md-card-content>
                            <DashboardTreeChart @tree-chart="(chart) => cell.chart = chart"
                                :districtualData="cell.data"
                                :regionalData="cell.parentData"
                                />
                        </md-card-content>
                        <md-card-actions>
                            <md-menu md-size="medium" md-direction="bottom-end">
                                <md-button class="md-icon-button" md-menu-trigger>
                                    <md-icon>file_download</md-icon>
                                </md-button>
                                <md-menu-content>
                                    <md-menu-item v-on:click="saveFeatureCollectionAsCsv(cell.data.datasets[0].data, cell.options)">Download as CSV</md-menu-item>
                                    <md-menu-item v-on:click="saveFeatureCollectionAsGeoJson(cell.data.datasets[0].data, cell.options)">Download as GeoJSON</md-menu-item>
                                    <md-menu-item v-on:click="saveAsPng(cell.options, cell.chart, `tree`)">Download as PNG</md-menu-item>
                                </md-menu-content>
                            </md-menu>
                        </md-card-actions>
                    </md-card>
                </template>

                <template v-else>
                    <md-card md-with-hover>
                        <ChartContainer :type="cell.type" :chartData="cell.data" :chartOptions="cell.options" :cellID="cell.id" @save-csv="saveCsv(cell.data, cell.options)"/>
                    </md-card>
                </template>
            </div>

        </div>
    </md-content>
</template>

<script>
/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
*/
import ChartContainer from './ChartContainer.vue';
import ChoroplethGeoChart from './Dashboard/ChoroplethGeoChart.vue';
import DashboardIndicatorSelect from './Dashboard/DashboardIndicatorSelect.vue';
import { getIndicatorKeys, getIndicatorNames} from "@/components/Dashboard/StrataDashboardHelperFunctions";
import DashboardStackedBarChart from './Dashboard/DashboardStackedBarChart.vue';
import TablesChart from './Dashboard/TablesCharts.vue';
import DashboardTreeChart from './Dashboard/DashboardTreeChart.vue';
import DashboardTable from './Dashboard/DashboardTable.vue';
import html2canvas from "html2canvas";
import * as topojson from "topojson-client";

function isString(x) {
    return Object.prototype.toString.call(x) === '[object String]';
}

export default {
    name: "Dashboard",
    props: {
        blocks: {
            type: Array,
            required: false
        }
    },
    data: () => ({
        cells: []
    }),
    components: {
        ChartContainer,
        ChoroplethGeoChart,
        DashboardIndicatorSelect,
        DashboardStackedBarChart,
        TablesChart,
        DashboardTreeChart,
        DashboardTable
    },
    mounted() {
        let previousSize;
        new ResizeObserver((entry) => {
            let dashboardContentWidth = entry[0].target.offsetWidth;

            if(dashboardContentWidth > 1000 && previousSize < 1000) {
                this.setCellWidths(50)
            } else if (dashboardContentWidth < 1000 && previousSize > 1000) {
                this.setCellWidths(100)
            }
            previousSize = dashboardContentWidth;
        }).observe(this.$refs.DashboardContainer)
    },
    methods: {
        saveFeatureCollectionAsCsv: function(featureCollection, options) {
            let features = featureCollection.features;
            let headers = Object.keys(features[0].properties)
            let rows = [headers]

            features.forEach(feature => {
                let data = []
                headers.forEach(header => {
                    let property = feature.properties[header]
                    data.push(property)
                })
                rows.push(data)
            })

            this.saveCsvFromRows(rows, options)
        },
        saveAsPng(options, chart, type) {
            let svgParent = null;
            let domURL = window.URL || window.webkitURL || window;
            let imageURI = null;

            if(type == "table") {
                let table = chart.getContainer().getElementsByTagName('table')[0];
                imageURI = domURL.createObjectURL(new Blob([svgParent], {type: 'image/svg+xml'}));
                html2canvas(table, {y: - table.offsetTop}).then(function(canvas) {
                    var img = canvas.toDataURL("image/png");

                    let filename = options.title.display ? options.title.text + ".png": "output.png";
                    let element = document.createElement('a');
                    element.setAttribute('href', `data:${'image/png'}; ` + img);
                    element.setAttribute('download', filename);
                    element.style.display = 'none';
                    document.body.appendChild(element);
                    element.click();
                    document.body.removeChild(element);
                });
            } else {
                svgParent = chart.getContainer().getElementsByTagName('svg')[0];
                svgParent.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
                imageURI = domURL.createObjectURL(new Blob([svgParent.outerHTML], {type: 'image/svg+xml'}));

                let imageCanvas = document.createElement('canvas');
                let img = document.createElement('div');
                img.style.display = 'none';
                document.body.appendChild(img);

                let image = new Image();
                image.onload = function() {
                    imageCanvas.setAttribute('width', parseFloat(svgParent.getAttribute('width')));
                    imageCanvas.setAttribute('height', parseFloat(svgParent.getAttribute('height')));
                    imageCanvas.getContext('2d').drawImage(image, 0, 0);
                    img.innerHTML = '<img src="' + imageCanvas.toDataURL('image/png') + '" />';

                    let filename = options.title.display ? options.title.text + ".png": "output.png";
                    let element = document.createElement('a');
                    element.setAttribute('href', `data:${'image/png'}; ` + img.firstElementChild.src);
                    element.setAttribute('download', filename);
                    element.style.display = 'none';
                    document.body.appendChild(element);
                    element.click();
                    document.body.removeChild(element);
                    document.body.removeChild(img);
                }
                image.src = imageURI;
            }
        },
        saveFeatureCollectionAsGeoJson: function (featureCollection, options) {
            let geoJSON = JSON.stringify(featureCollection)
            let filename = options.title.display ? options.title.text + ".geojson" : "featurecollection.geojson";

            this.saveFile(geoJSON, filename, 'application/geo+json')
        },
        saveCsv: function(data, options) {
            let saveData = JSON.parse(JSON.stringify(data));
            saveData.labels.unshift('Dataset Names');
            let headers = saveData.labels;
            let rows = [headers];
            // const replacer = (key, value) => value === null ? '' : value // null handler - TODO needs to be added
            saveData.datasets.forEach((dataset) => {
              dataset.data.unshift(dataset.label);
              const semiColonDelineatedData = dataset.data.map(function (item) {
                if (isString(item)) {
                  return item.replace(',', ';')
                }
                return item
              })
              rows.push(semiColonDelineatedData);
            })
            this.saveCsvFromRows(rows, options)
        },
        saveCsvFromRows(rows, options){
            let csv = rows.map(row => row.join(','));
            csv = csv.join('\n');

            // workaround for downloading file
            let filename = options.title.display ? options.title.text + ".csv" : "table.csv";
            this.saveFile(csv, filename, 'text/csv')
        },
        saveFile(file, filename, mediaType) {
            let element = document.createElement('a');
            element.setAttribute('href', `data:${mediaType};charset=utf-8,${encodeURIComponent(file)}`);
            element.setAttribute('download', filename);

            element.style.display = 'none';
            document.body.appendChild(element);

            element.click();

            document.body.removeChild(element);
        },
        createCell: function(output, type, options, allowedTypes, bbox, cellID) {
            let exceptionCharts = ['doughnut', 'pie']
            if(allowedTypes === undefined || allowedTypes.includes(type)){
                let cellOptions = JSON.parse(JSON.stringify(options)); // deep object copy

                if (exceptionCharts.includes(type)) {
                    cellOptions = {...cellOptions, maintainAspectRatio: false, responsive: true, scales: {xAxes: [{display: false}], yAxes: [{display: false}]}}
                }
                cellOptions = {...cellOptions, maintainAspectRatio: false, responsive: true}
                let cellOutput = JSON.parse(JSON.stringify(output));
                cellOptions.title.text = output.outputTitle;

                if (output.rowColumnHeader && cellOptions.rowColumnHeader){
                    if(cellOptions.rowColumnHeader.display) cellOptions.rowColumnHeader.text = output.rowColumnHeader;
                }

                if (output.xTitle !== undefined && output.xTitle !== null){
                    if(output.xTitle.length && output.xTitle.length > 0){
                        cellOptions.scales.xAxes[0].scaleLabel = {
                            display: true,
                            labelString: output.xTitle
                        }
                    }
                }

                if (output.yTitle !== undefined && output.yTitle !== null){
                    if(output.yTitle.length && output.yTitle.length > 0){
                        cellOptions.scales.yAxes[0].scaleLabel = {
                            display: true,
                            labelString: output.yTitle
                        }
                    }
                }

                if(type === "histogram"){
                    type = "bar";
                    let noLabels = cellOutput.data.labels.length
                    if (cellOutput.data.datasets.length === 1) {
                        cellOutput.data.datasets[0].barPercentage = 1.25;
                        cellOutput.data.datasets[0].borderWidth = 0;
                    } else if (cellOutput.data.datasets.length > 1) {
                        cellOutput.data.datasets.forEach(dataset => {
                            dataset.barPercentage = 1;
                        })
                    }
                    cellOptions.scales = {
                        xAxes: [
                        {
                            display: false,
                            ticks: {
                                max: cellOutput.data.labels[noLabels-2].toFixed(3),
                            },
                        },
                        {
                            display: true,
                            ticks: {
                                autoSkip: true,
                                max: cellOutput.data.labels[noLabels-1].toFixed(3),
                            },
                        },
                        ],
                        yAxes: [{
                            ticks: {
                                beginAtZero:true
                            }
                        }]
                    }
                } else {
                    cellOutput.data.datasets.forEach(dataset => {
                        dataset.borderWidth = 1;
                        dataset.fill = cellOptions.fill;
                    })
                }

                let cell = {
                    id: cellID,
                    type: type,
                    data: cellOutput.data,
                    options: cellOptions,
                    bbox: bbox,
                    outputTitle: cellOutput.outputTitle,
                    cellSizeString: `md-size-${100}`
                }

                if(cellOutput.dataType && cellOutput.dataType === 'Feature Collection') {
                    // create cell selectedIndicatorKey, indicator names
                    cell.indicatorKeysAndNames = this.getIndicatorKeysAndNames(cellOutput);
                    cell.selectedIndicatorName = cell.indicatorKeysAndNames.names[0];
                    cell.selectedIndicatorKey = cell.indicatorKeysAndNames.keys[0];
                }

                if(type === 'tree chart') {
                    // get cell where outputTitle is the same as this cells parents
                    // grab the data
                    let index = this.cells.findIndex((searchCell) => searchCell.outputTitle === cellOutput.parent)

                    if(index !== -1) {
                        let parentData = this.cells[index].data;
                        cell.parentData = parentData;
                    } else {
                        console.error(`tried to find parent of cell ${cell.outputTitle}-${cell.type} but couldn't find it`)
                        return;
                    }
                }

                this.cells.push(cell);
            }
        },
        clearCells() {
            this.cells = [];
            this.blocksWithData = []
        },
        getIndicatorKeysAndNames(cellOutput) {
            let indicatorKeysAndNames = {
                keys: [],
                names: []
            }

            let feature_collection = cellOutput.data.datasets[0].data;
            let indicator_keys = getIndicatorKeys(feature_collection);
            let indicator_names = getIndicatorNames(indicator_keys);
            indicatorKeysAndNames.keys = indicator_keys;
            indicatorKeysAndNames.names = indicator_names;

            // checks for property of feature collection to see for a default label to set the indicator to
            if(feature_collection.properties && feature_collection.properties.default_property_name){
                indicatorKeysAndNames.keys.unshift("region_value")
                indicatorKeysAndNames.names.unshift(feature_collection.properties.default_property_name)
            } else {
                // adds the default keys and names for the total STRATA score
                indicatorKeysAndNames.keys.unshift("region_value")
                indicatorKeysAndNames.names.unshift("STRATA SCORE")
            }

            return indicatorKeysAndNames
        },
        getCellIndexByCellID(cellID) {
            return this.cells.findIndex((cell) => cell.id === cellID)
        },
        getIndicatorKeyFromIndicatorName(indicatorName, indicatorKeysAndNames){
            let index = indicatorKeysAndNames.names.indexOf(indicatorName);
            if (index !== -1){
                return indicatorKeysAndNames.keys[index];
            } else {
                console.error(`tried to get key of indicator with name ${indicatorName} but it could not be found`)
            }
        },
        updateSelectedIndicatorForCell(newIndicator, cellID) {
            let indexOfCell = this.getCellIndexByCellID(cellID)

            if(indexOfCell !== -1) {
                this.cells[indexOfCell].selectedIndicatorName = newIndicator;
                this.cells[indexOfCell].selectedIndicatorKey = this.getIndicatorKeyFromIndicatorName(newIndicator, this.cells[indexOfCell].indicatorKeysAndNames)
            } else {
                console.error("Could not find index of cell, could not update selected Indicator, CellID is ", cellID)
            }
        },
        setCellWidths(size) {
            let totalCells = 0;
            let tableCells = 0;
            let lastNonTableIndex;
            this.cells.forEach((cell, index) => {
                totalCells++;
                if(cell.type === 'table' || cell.type === 'strata table'){ // tables should always be full width
                    tableCells++;
                    cell.cellSizeString = 'md-size-100'
                } else {
                    lastNonTableIndex = index;
                    cell.cellSizeString = `md-size-${size}`
                }
            })

            if((totalCells - tableCells) % 2 == 1){
                // odd number of cells set last cell to be full width
                this.cells[lastNonTableIndex].cellSizeString = `md-size-100`
            }
         }
    },
    computed: {
        blocksWithData: {
            get: function() {
                let dataBlocks = this.blocks.filter(block => {
                    if(block.runResult.layers && block.runResult.charts && block.runResult.outputs){
                        return (block.runResult.outputs.length > 0 && block.runResult.charts.length > 0);
                    }
                    return false;
                });
                return dataBlocks;
            },
            set: function(newValue) {
                return newValue;
            }
        }
    },
    watch: {
        blocksWithData: {
            deep: false,
            immediate: true,
            handler(blocks) {
                let currentCellIDs = this.cells.map(cell => cell.id);

                blocks.forEach(block => {
                    let outputData = block.runResult.outputs;
                    let charts = JSON.parse(block.runResult.charts)
                    let bbox = null;
                    if(block.runResult.bbox){
                        bbox = block.runResult.bbox;
                    }
                    outputData.forEach(output => {
                        let allowedTypes = output.allowedTypes;

                        if(output.dataType) {
                            if(output.dataType === 'Feature Collection') {
                                // if the output is a feature collection we need to convert topology to geojson
                                output.data.datasets.forEach(dataset => {
                                    let topo = JSON.parse(dataset.data);
                                    console.log("topo", topo)
                                    let feature = topojson.feature(topo, topo.objects.data)
                                    console.log("feature is", feature)
                                    dataset.data = feature;
                                })
                            }
                        }

                        charts.forEach(chart => {
                            let cellID = block.id + '-' + output.outputTitle + '-' + chart.type;
                            if(!currentCellIDs.includes(cellID)){
                                this.createCell(output, chart.type, chart.options, allowedTypes, bbox, cellID);
                            }
                        })
                    })

                })
            }
        },
    },
    created(){
        this.$root.$on('oa-dashboard-reload', () => {
            this.clearCells();
        });
    }
}
</script>

<style>
.md-layout {
    width: 100%;
}

.md-card {
    margin: 4px;
    vertical-align: top;
    width:100%;
}

.stacked-bar-chart {
    min-height: 500px;
}

.dashboard-tree-chart {
    min-height: 500px;
}
</style>
